import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, Trash2, ShoppingCart, ChevronRight, MapPin, Bike, Store } from 'lucide-react';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import DeliveryFeeBreakdown from '../components/DeliveryFeeBreakdown';
import AddressAutocomplete from '../components/AddressAutocomplete';
import SavedAddressPicker from '../components/SavedAddressPicker';
import api from '../services/api';
import { toast } from 'sonner';

const Cart = () => {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { cart, updateQty, removeFromCart, cartTotal, format, delivery, setDelivery, fulfillment, setFulfillment, restoreCartBackup } = useApp();
  const ccy = cart.vendorCurrency;
  const [quote, setQuote] = useState(null);
  const [pickedId, setPickedId] = useState(null);
  const isPickup = fulfillment === 'pickup';

  // A cancelled/failed card payment lands back here — restore the basket so
  // the customer can simply try again.
  useEffect(() => {
    if (params.get('payment') !== 'cancelled') return;
    const restored = restoreCartBackup();
    toast.error(restored
      ? 'Payment not completed — your basket was restored so you can try again.'
      : 'Payment not completed.');
    setParams({}, { replace: true });
    // eslint-disable-next-line
  }, []);

  // Real delivery-fee quote (km, base + per-km breakdown) before placing the order.
  useEffect(() => {
    if (isPickup || !cart.vendorId || !cart.items.length || delivery?.lat == null || delivery?.lng == null) { setQuote(null); return; }
    let alive = true;
    api.post('/orders/quote', { vendor_id: cart.vendorId, dest_lat: delivery.lat, dest_lng: delivery.lng })
      .then((r) => { if (alive) setQuote(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [cart.vendorId, cart.items.length, delivery?.lat, delivery?.lng, isPickup]);

  const chooseAddress = (address, lat, lng, savedId = null) => {
    setDelivery({ address, lat, lng });
    setPickedId(savedId);
  };

  const deliveryFee = isPickup ? 0 : (cart.items.length ? (quote?.delivery_fee ?? 2.99) : 0);
  const serviceFee = cart.items.length ? Math.max(1, cartTotal * 0.05) : 0;
  const grand = cartTotal + deliveryFee + serviceFee;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showSearch={false} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => nav(-1)} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Continue browsing
        </button>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Your cart</h1>
        {cart.vendorName && <p className="text-slate-500 mt-1">From <b className="text-slate-700">{cart.vendorName}</b></p>}

        {cart.items.length === 0 ? (
          <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <div className="h-14 w-14 rounded-full bg-slate-100 mx-auto flex items-center justify-center">
              <ShoppingCart className="h-6 w-6 text-slate-400" />
            </div>
            <h2 className="text-lg font-medium text-slate-900 mt-4">Your cart is empty</h2>
            <p className="text-slate-500 text-sm mt-1">Add items from a restaurant to get started.</p>
            <Link to="/">
              <Button className="mt-5 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="browse-restaurants-btn">Browse restaurants</Button>
            </Link>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
              {cart.items.map((i) => (
                <div key={i.id} className="flex items-center gap-4 p-4" data-testid={`cart-item-${i.id}`}>
                  {i.image && <img src={i.image} alt={i.name} className="h-16 w-16 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{i.name}</p>
                    <p className="text-sm text-slate-500">{format(i.price, ccy)} each</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(i.id, -1)} className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50" data-testid={`cart-qty-minus-${i.id}`}>
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-6 text-center font-medium">{i.qty}</span>
                    <button onClick={() => updateQty(i.id, 1)} className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50" data-testid={`cart-qty-plus-${i.id}`}>
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="w-20 text-right font-medium text-slate-900">{format(i.price * i.qty, ccy)}</div>
                  <button onClick={() => removeFromCart(i.id)} className="text-slate-400 hover:text-rose-500" data-testid={`cart-remove-${i.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-5 h-fit sticky top-20">
              {/* Deliver-to: pickup/delivery toggle + address so fees show instantly */}
              <div className="mb-4" data-testid="cart-deliver-to">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-slate-900">Deliver to</h2>
                  <div className="inline-flex rounded-full border border-slate-200 p-0.5 bg-slate-50" data-testid="cart-fulfillment-toggle">
                    <button
                      type="button"
                      onClick={() => setFulfillment('delivery')}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 transition-colors ${!isPickup ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                      data-testid="cart-mode-delivery"
                    >
                      <Bike className="h-3 w-3" /> Delivery
                    </button>
                    <button
                      type="button"
                      onClick={() => setFulfillment('pickup')}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 transition-colors ${isPickup ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                      data-testid="cart-mode-pickup"
                    >
                      <Store className="h-3 w-3" /> Pickup
                    </button>
                  </div>
                </div>
                {isPickup ? (
                  <p className="text-xs text-slate-500 mt-2" data-testid="cart-pickup-note">
                    You'll pick up at the store — no delivery fee.
                  </p>
                ) : (
                  <div className="mt-3 space-y-2">
                    <SavedAddressPicker
                      selectedId={pickedId}
                      onPick={(a) => chooseAddress(a.address, a.lat, a.lng, a.id)}
                      onClear={() => setPickedId(null)}
                    />
                    <AddressAutocomplete
                      value={pickedId ? '' : (delivery?.address || '')}
                      onChange={(addr, geo) => chooseAddress(addr, geo?.lat ?? null, geo?.lng ?? null, null)}
                      placeholder="Enter delivery address…"
                      className="h-10"
                    />
                    {delivery?.address && delivery?.lat != null && (
                      <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1 inline-flex items-center gap-1" data-testid="cart-address-confirmed">
                        <MapPin className="h-3 w-3" /> {delivery.address}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="h-px bg-slate-200 mb-4" />
              <h2 className="font-semibold text-slate-900">Order summary</h2>
              <div className="mt-4 space-y-2 text-sm">
                <Row label="Subtotal" value={format(cartTotal, ccy)} />
                {!isPickup && <Row label={quote ? 'Delivery fee' : 'Delivery fee (estimate)'} value={format(deliveryFee, ccy)} />}
                {isPickup && <Row label="Delivery fee" value={format(0, ccy)} />}
                {!isPickup && quote && (
                  <DeliveryFeeBreakdown
                    breakdown={quote.breakdown}
                    distanceKm={quote.distance_km}
                    currency={quote.currency || ccy}
                    total={quote.delivery_fee}
                  />
                )}
                <Row label="Service fee" value={format(serviceFee, ccy)} />
                <div className="h-px bg-slate-200 my-2" />
                <Row label="Total" value={format(grand, ccy)} bold />
              </div>
              {!isPickup && !quote && (
                <p className="text-[11px] text-slate-400 mt-2">Set your delivery address above to see the exact per-km fee breakdown.</p>
              )}
              <Button onClick={() => nav('/checkout')} className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11" data-testid="proceed-checkout-btn">
                Go to checkout <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              <p className="text-xs text-slate-400 mt-3 text-center">Taxes calculated at checkout</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Row = ({ label, value, bold }) => (
  <div className={`flex items-center justify-between ${bold ? 'font-semibold text-slate-900 text-base' : 'text-slate-600'}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

export default Cart;
